import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Button, Tag, message, Modal, Input, Space, Card } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, EditOutlined, StopOutlined, CheckCircleOutlined, KeyOutlined } from '@ant-design/icons';
import { superCompanyApi } from '../../services/api';
import AdminFormModal from './AdminFormModal';

const CompanyAdmins = () => {
  const { id: companyId } = useParams();
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetAdminId, setResetAdminId] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [adminRes, companyRes] = await Promise.all([
        superCompanyApi.listAdmins(companyId),
        superCompanyApi.detail(companyId),
      ]);
      setAdmins(adminRes.data.list || []);
      setCompany(companyRes.data);
    } catch (err) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [companyId]);

  const handleCreateAdmin = async (values) => {
    try {
      await superCompanyApi.createAdmin(companyId, values);
      message.success('管理员创建成功');
      setModalOpen(false);
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || '创建失败');
    }
  };

  const handleUpdateAdmin = async (values) => {
    try {
      await superCompanyApi.updateAdmin(companyId, editingAdmin.id, {
        nickname: values.nickname,
        is_active: values.is_active ? 1 : 0,
      });
      message.success('管理员更新成功');
      setModalOpen(false);
      setEditingAdmin(null);
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || '更新失败');
    }
  };

  const handleToggleAdmin = async (admin) => {
    const action = admin.is_active ? '禁用' : '启用';
    Modal.confirm({
      title: `确认${action}`,
      content: `确定要${action}管理员 "${admin.username}" 吗？`,
      onOk: async () => {
        try {
          await superCompanyApi.toggleAdmin(companyId, admin.id);
          message.success(`已${action}`);
          fetchData();
        } catch (err) {
          message.error(err.response?.data?.message || '操作失败');
        }
      },
    });
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      message.error('密码至少6位');
      return;
    }
    try {
      await superCompanyApi.resetAdminPassword(companyId, {
        admin_id: resetAdminId,
        new_password: newPassword,
      });
      message.success('密码重置成功');
      setResetModalOpen(false);
      setNewPassword('');
    } catch (err) {
      message.error(err.response?.data?.message || '重置失败');
    }
  };

  const columns = [
    { title: '用户名', dataIndex: 'username', key: 'username' },
    { title: '昵称', dataIndex: 'nickname', key: 'nickname' },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (v) => v ? <Tag color="green">启用</Tag> : <Tag color="red">禁用</Tag>,
    },
    {
      title: '最后登录',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      render: (v) => v || '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingAdmin(record);
              setModalOpen(true);
            }}
          >
            编辑
          </Button>
          <Button
            size="small"
            danger={record.is_active}
            icon={record.is_active ? <StopOutlined /> : <CheckCircleOutlined />}
            onClick={() => handleToggleAdmin(record)}
          >
            {record.is_active ? '禁用' : '启用'}
          </Button>
          <Button
            size="small"
            icon={<KeyOutlined />}
            onClick={() => {
              setResetAdminId(record.id);
              setResetModalOpen(true);
            }}
          >
            重置密码
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/super/companies')}>
              返回公司列表
            </Button>
            <span style={{ fontSize: 18, fontWeight: 600 }}>
              {company?.name || ''} - 管理员列表
            </span>
          </Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingAdmin(null);
              setModalOpen(true);
            }}
          >
            新增管理员
          </Button>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={admins}
          loading={loading}
          pagination={false}
        />
      </Card>

      <AdminFormModal
        open={modalOpen}
        admin={editingAdmin}
        companyId={companyId}
        onCancel={() => {
          setModalOpen(false);
          setEditingAdmin(null);
        }}
        onOk={editingAdmin ? handleUpdateAdmin : handleCreateAdmin}
      />

      <Modal
        title="重置密码"
        open={resetModalOpen}
        onCancel={() => {
          setResetModalOpen(false);
          setNewPassword('');
        }}
        onOk={handleResetPassword}
      >
        <Input.Password
          placeholder="请输入新密码（至少6位）"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default CompanyAdmins;
